import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChange, ViewChild } from '@angular/core';
import { JwtLegFiClaims } from '../../../services/auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../../../services/auth/legfi-jwt.service';
import { DatatableConfig } from './datatable.config';

@Component({
    selector: 'lf-datatable',
    styleUrls: ['./datatable.scss'],
    templateUrl: './datatable.component.html',
})
export class DatatableComponent implements AfterViewInit, OnChanges, OnInit
{
    @ViewChild('datatableHeader', {read: ElementRef})
    public datatableHeader: any;
    @Input()
    public hideHeader: boolean = false;

    @ViewChild('datatable', {read: ElementRef})
    public datatable: any;

    @Input()
    public data: any[] = [];
    @Output()
    public dataChange: EventEmitter<any> = new EventEmitter();

    public length: number = 0;
    public resultsLength: number = 0;

    public initialized: boolean = false;
    public lastSearch: string = '';

    @ViewChild('datatableFooter', {read: ElementRef})
    public datatableFooter: any;

    @Input()
    public showFooter: boolean = false;

    @Input()
    public hasSeparateCheckColumn: boolean = false;

    @Input()
    public hideSums: boolean = false;
    public summable: boolean = false;
    public smallHide: boolean = false;
    public sums: { [key: string]: number } = {};

    @Input()
    public showActionColumn: boolean = false; // readonly permissions need action column hidden
    @Input()
    public exportable: boolean = false;
    @Input()
    public exportFilename: string = 'table-dump.csv';

    @Input()
    public config: DatatableConfig = <DatatableConfig>{};

    @Output()
    configChange: EventEmitter<DatatableConfig> = new EventEmitter<DatatableConfig>();

    public jwt: JwtLegFiClaims;

    ngOnInit() {
        this.length = this.data.length;
        this.jwt = LegFiJwtService.read();

        this.showActionColumn = !(this.jwt && !(this.jwt.superUser || this.jwt.admin) && !this.showActionColumn);
    }

    ngOnChanges(changes: { [propertyName: string]: SimpleChange }) {
        // This change event fires for the first time when the control is passed into the directive.
        if (changes.hasOwnProperty('data')) {
            this.filterResults();
        }
    }

    ngAfterViewInit() {
    }

    public changeConfig(type: string, config: any) {
        this.config[type] = config;
        this.configChange.emit(this.config);
        this.filterResults();
    }

    public filterResults() {
        this.changeFilter();
        this.changeSort();

        this.length = this.data.length;
        this.dataChange.emit(this.data);
        this.resultsLength = this.data
                ? this.data.filter((row: any) => {
                    return !row.hidden;
                }).length
                : 0;

        this.determineSums(this.data);
        this.initialized = true;
    }

    public determineSums(data: any[]) {
        this.summable = !!(
                data &&
                data.length &&
                this.config.columns.filter((field) => {
                    return field.hasOwnProperty('sum') && field.sum;
                }).length
        );

        if (this.summable) {
            const sums: { [key: string]: number } = {};
            const self = this;
            this.config.columns
                    .filter((field) => {
                        return field.hasOwnProperty('sum') && field.sum;
                    })
                    .forEach((field) => {
                        sums[field.key] = Number(self.sumColumn(field, data));
                    });
            this.sums = sums;
        }
    }

    public changeFilter() {
        if (!(this.config.filtering && this.config.filtering.filter)) {
            this.lastSearch = '';

            // Un-hide everything.
            this.data.forEach((item: any) => {
                item.hidden = false;
            });

            return;
        }

        /**
         * Find all the trs that belong to this datatable in the DOM.
         */
        const trs: any = this.datatable.nativeElement.querySelectorAll('tbody tr');
        const elementsPerDataRow: number = this.data.length > 0 ? Math.round(trs.length / this.data.length) : 1;
        let searchString: string = this.config.filtering.filter.trim();
        const subSearch: boolean =
                searchString !== '' && searchString.substring(0, this.lastSearch.length) === this.lastSearch;

        /**
         * Abort early if search query did not change.
         */
        if (this.lastSearch !== '' && searchString === this.lastSearch) {
            return;
        }

        // Reset hidden flags if not sub-searching.
        if (!subSearch) {
            // Reset hidden status since this is not a sub-search. We will need to rescan every row.
            this.data.forEach((item: any) => {
                item.hidden = false;
            });
        }

        /**
         * Regex to search for individual terms, all required, order irrelevant
         */
        searchString = searchString.replace(/[\-\[\]\/{}()*+?.\\^$|]/g, '\\$&'); // Regex escape
        const val = '^(?=.*\\b' + searchString.split(/\s+/).join('\\b)(?=.*\\b') + ').*$';
        const reg = RegExp(val, 'i');

        /**
         * Scan the data column definitions and see which columns are searchable.
         */
        const searchableColumns: number[] = [];
        const searchableByData: {} = {};
        this.config.columns.forEach((item: any, i: number) => {
            if (item.hasOwnProperty('searchable') && item.searchable) {
                searchableColumns.push(i);
                if (item.hasOwnProperty('searchData') && item.searchData) {
                    searchableByData[i] = item.key;
                }
            }
        });

        if (searchableColumns.length === 0 && trs.length > 0) {
            for (let c = 0; c < trs[0].children.length; c++) {
                searchableColumns.push(c);
            }
        }

        const self = this;

        // Set new last search element.
        this.lastSearch = searchString;
        this.data.forEach(function (item: any, i) {
            /**
             * If this search is simply the last search + additional characters,
             *  dont bother rechecking this row.
             */
            if ((subSearch && (item.hasOwnProperty('hidden') && item.hidden)) || trs.length <= i * elementsPerDataRow) {
                return;
            }

            // Create string of this row's rendered HTML.
            let searchableColumnData: NodeList = trs[i * elementsPerDataRow].children;
            let textContent: string = '';

            // Search root row
            for (let a = 0; a < searchableColumnData.length; a++) {
                if (typeof searchableByData[a] !== 'undefined') {
                    textContent += self.getValueAtPath(item, searchableByData[a]);
                } else {
                    if (searchableColumns.indexOf(a) !== -1) {
                        textContent += searchableColumnData[a].textContent.replace(/\s+/g, ' ');
                    }
                }
            }

            // If each 'item' generates multiple trs, scan additional ones also.
            const l = i * elementsPerDataRow + elementsPerDataRow - 1;
            for (let k = i; k < l; k++) {
                if (trs.length > k) {
                    searchableColumnData = trs[k].children;
                    for (let a = 0; a < searchableColumnData.length; a++) {
                        if (searchableColumns.indexOf(a) !== -1) {
                            textContent += searchableColumnData[a].textContent.replace(/\s+/g, ' ');
                        }
                    }
                }
            }

            // Match the rendered HTML against the search query.
            // Ordering does not matter, but each search term must be present (AND)
            item.hidden = !reg.test(textContent);
        });
    }

    public changeSort() {
        if (!this.config.columns) {
            return this.data;
        }

        const columns = this.config.columns || [];
        let columnName: string = '';
        let sort: string = '';
        let found: boolean = false;
        let sortBy: any[] = [];

        for (let i = 0; i < columns.length; i++) {
            if (typeof columns[i].sort !== 'undefined' && columns[i].sort !== false && columns[i].sort !== '') {
                columnName = columns[i].key;
                sort = columns[i].sort;
                sortBy = columns[i].sortBy;
                found = true;
            }
        }

        if (!found) {
            return this.data;
        }

        // Simple sort.
        // Allow sorts on multiple fields (AND functionality)
        const sortColumns = columnName.split(',');
        this.data = this.data.sort((previous: any, current: any) => {
            for (let s: number = 0; s < sortColumns.length; s++) {
                const move = this.sortFunction(sortColumns, s, sort, sortBy, previous, current);
                if (move !== 0) {
                    return move;
                }
            }
            return 0;
        });
    }

    /**
     * @param {any} column
     * @param {any[]} data
     * @returns {number}
     */
    public sumColumn(column: any, data: any[]) {
        if (!column || !column.sum) {
            return '';
        }

        if (!data || !data.length) {
            return 0;
        }
        const summableData = data.filter((row) => {
            return row.hasOwnProperty(column.key) && (!row.hasOwnProperty('hidden') || !row.hidden);
        });
        if (summableData.length < 1) {
            return 0;
        }
        // if (summableData[0].hasOwnProperty('activeAfter')) {
        //     const now = new Date();
        //     return summableData
        //         .filter((row) => {
        //             const activeAfter = new Date(row.activeAfter);

        //             return activeAfter < now;
        //         })
        //         .map((row) => {
        //             return Number(row[column.key]);
        //         })
        //         .reduce((previous, current) => {
        //             return previous + current;
        //         }, 0);
        // }

        return summableData
                .map((row) => {
                    return Number(row[column.key]);
                })
                .reduce((previous, current) => {
                    return previous + current;
                }, 0);
    }

    public exportData() {
        const dataRows: NodeListOf<HTMLTableRowElement> = this.datatable.nativeElement.querySelectorAll(
                'tbody tr:not([hidden])',
        );
        const rows: string[][] = [];
        let skipFirstColumn: boolean = false;

        // Extract data for header row if available.
        if (this.datatableHeader && this.datatableHeader.nativeElement) {
            const headerRows: HTMLTableRowElement = this.datatableHeader.nativeElement.querySelector(
                    'thead tr:first-child',
            );
            if (headerRows && headerRows.cells) {
                const headerCells: NodeListOf<Element> = headerRows.querySelectorAll(
                        'th:not(.checkbox-cell):not(.row-actions)',
                );
                const headerRow: string[] = [];
                for (let h: number = 0; h < headerCells.length; h++) {
                    const content = headerCells[h].textContent;
                    if (h === 0 && (!content || content.indexOf('checkbox') !== -1)) {
                        skipFirstColumn = true;
                        continue;
                    }

                    headerRow.push(content);
                }

                rows.push(headerRow);
            }
        }

        // Extract data for data rows if available.
        for (let r: number = 0; r < dataRows.length; r++) {
            const cells: NodeListOf<Element> = dataRows[r].querySelectorAll(
                    'td:not([colspan]):not(.checkbox-cell):not(.row-actions)',
            );
            if (!cells.length) {
                continue;
            }

            const start = skipFirstColumn ? 1 : 0;
            const row: string[] = [];

            for (let c: number = start; c < cells.length; c++) {
                if (cells[c].innerHTML.match('class="avatar initials')) {
                    cells[c].children[0].innerHTML = '';
                }
                row.push(cells[c].textContent);
            }

            rows.push(row);
        }

        // Rows object complete, convert to a CSV.
        const escapeRow = function (row: string[]) {
            for (let i: number = 0; i < row.length; i++) {
                row[i] = row[i].trim().replace(/"/g, '""');
                if (row[i].search(/([",\n])/g) >= 0) {
                    row[i] = '"' + row[i] + '"';
                }
            }

            return row;
        };

        // Implode columns on comma, rows on carriage return + newline to create file string.
        const csv = rows
                .map((row) => {
                    return escapeRow(row).join(',');
                })
                .join('\r\n');

        // Create file object for download.
        const blob = new Blob([csv], {type: 'text/csv;charset:utf-8;'});
        // IE 10+
        if (navigator.msSaveBlob) {
            navigator.msSaveBlob(blob, this.exportFilename);
        } else {
            const link = document.createElement('a');

            // Feature detection.
            if (link.download !== undefined) {
                // Browsers that support HTML5 download attribute.
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', this.exportFilename);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        }
    }

    public getValueAtPath(obj, path, def: string = '') {
        /**
         * If the path is a string, convert it to an array
         * @param  {String|Array} path The path
         * @return {Array}             The path array
         */
        const stringToPath = function (str) {

            // If the path isn't a string, return it
            if (typeof str !== 'string') {
                return str;
            }

            // Create new array
            const output = [];

            // Split to an array with dot notation
            str.split('.').forEach(function (item, index) {
                // Split to an array with bracket notation
                item.split(/\[([^}]+)\]/g).forEach(function (key) {
                    // Push to the new array
                    if (key.length > 0) {
                        output.push(key);
                    }

                });

            });

            return output;

        };

        // Get the path as an array
        path = stringToPath(path);

        // Cache the current object
        let current = obj;

        // For each item in the path, dig into the object
        for (let i = 0; i < path.length; i++) {
            // If the item isn't found, return the default (or null)
            if (!current[path[i]]) {
                return def;
            }

            // Otherwise, update the current  value
            current = current[path[i]];
        }

        return current;
    }

    private sortFunction(
            sortColumns: string[],
            column: number,
            direction: string,
            sortBy: any[],
            previous: any,
            current: any,
    ) {
        // Check sort definition language for the '|' character. This allows you to specify multiple sorts,
        // left-to-right, with subsequent searches only being performed if the specified field is null or undefined.
        const columnOptions = sortColumns[column].split('|');

        for (let c: number = 0; c < columnOptions.length; c++) {
            // Split up path to sort property by dot notation.
            const columnPathParts: string[] = columnOptions[c].split('.');

            // Set up search sides.
            let left = previous;
            let right = current;

            // Navigate dot notation definition looking for sort property.
            for (let i: number = 0; i < columnPathParts.length - 1; i++) {
                if (left.hasOwnProperty(columnPathParts[i])) {
                    left = left[columnPathParts[i]];
                }
                if (right.hasOwnProperty(columnPathParts[i])) {
                    right = right[columnPathParts[i]];
                }
            }

            if (
                    !(
                            left &&
                            columnPathParts[columnPathParts.length - 1] &&
                            left[columnPathParts[columnPathParts.length - 1]]
                    )
            ) {
                return direction === 'desc' ? 1 : -1;
            } else if (
                    !(
                            right &&
                            columnPathParts[columnPathParts.length - 1] &&
                            right[columnPathParts[columnPathParts.length - 1]]
                    )
            ) {
                return direction === 'desc' ? -1 : 1;
            }

            // Perform sort against this property.
            let l = left[columnPathParts[columnPathParts.length - 1]];
            let r = right[columnPathParts[columnPathParts.length - 1]];

            if (typeof l === 'string') {
                l = l.toLocaleLowerCase();
                l = this.normalizeMixedDataValue(l);
            } else if (l instanceof Array) {
                l = l.length;
            } else if (sortBy && sortBy.length > 0) {
                let string = '';
                sortBy.map((sortProp) => {
                    string += left[column][sortProp];
                });

                l = string;
                l = l.toLocaleLowerCase();
                l = this.normalizeMixedDataValue(l);
            }
            if (typeof r === 'string') {
                r = r.toLocaleLowerCase();
                r = this.normalizeMixedDataValue(r);
            } else if (r instanceof Array) {
                r = r.length;
            } else if (sortBy && sortBy.length > 0) {
                let string = '';
                sortBy.map((sortProp) => {
                    string += right[column][sortProp];
                });

                r = string;
                r = r.toLocaleLowerCase();
                r = this.normalizeMixedDataValue(r);
            }

            if (l === r) {
                return 0;
            }
            if (r === null || l > r) {
                return direction === 'desc' ? -1 : 1;
            } else if (l || l < r) {
                return direction === 'desc' ? 1 : -1;
            }
        }
        return 0;
    }

    private normalizeMixedDataValue(value) {
        const padding = '000000000000000';

        value = value.toString();
        value = value.replace(/(\d+)((\.\d+)+)?/g, ($0, integer, decimal, $3) => {
            if (decimal !== $3) {
                return padding.slice(integer.length) + integer + decimal;
            }
            decimal = decimal || '.0';

            return padding.slice(integer.length) + integer + decimal + padding.slice(decimal.length);
        });
        return value;
    }
}
